import {
    Box,
    Button,
    IconButton,
    LinearProgress,
    Stack,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Tooltip,
    Typography,
} from "@mui/material";
import { FunctionComponent, useEffect, useState } from "react";
import { LoadingButton } from "@mui/lab";
import { CertificationFileUploadComponent } from "./CertificationFileUploadComponent";
import { FileTypeIcon } from "../../common/components/FileTypeIcon";
import { getFileNameWithOutExt } from "../../common/utilities/utils";
import DeleteIcon from '@mui/icons-material/Delete';
import CloudDownloadIcon from '@mui/icons-material/CloudDownload';
import AutoFixHighIcon from '@mui/icons-material/AutoFixHigh';
import { ICertificationDeleteRequest, ICertificationFile, ICertificationGenerateRequest } from "./schoolCertificationModel";
import { toast } from "react-toastify";
import HallTicketService from "./hallTicketService";

export const HallTicketGeneration: FunctionComponent = () => {
    const isAddFilesVisible = true;
    const isDeleteFilesVisible = true;
    const [fileUploadIsInProgress, setFileUploadIsInProgress] = useState<boolean>(false);
    const [certificationFiles, setCertificationFiles] = useState<ICertificationFile[]>([]);
    const [filesLoading, setFilesLoading] = useState<boolean>(false);
    const [tabValue, setTabValue] = useState(0);
    const handleFileUpload = (e: React.ChangeEvent<HTMLInputElement>) => {

        const uploadedFiles = Array.from(e.target.files || []);
        setFileUploadIsInProgress(true);
        HallTicketService.fileUpload(uploadedFiles)
            .then((response) => {
                if (response.data.errors !== undefined && response.data.errors !== null && response.data.errors.length > 0) {
                    toast.error("error in uploading the files");
                } else {
                    setCertificationFiles([...certificationFiles, ...response.data.list]);
                    toast.success("file uploaded successfully");
                }
            }).finally(() => {
                setFileUploadIsInProgress(false);
            });
    }
    const loadCertificationFiles = () => {
        setFilesLoading(true);
        HallTicketService.getFiles().then((result) => {
            if (result.data.errors && result.data.errors.length > 0) {
                toast.error(result.data.errors[0].message);
            } else {
                setCertificationFiles(result.data.list);
            }

        }).finally(() => {
            setFilesLoading(false);
        });
    }

    useEffect(() => {
        loadCertificationFiles();
    }, [])

    const handleFileDelete = (fileDetails: ICertificationFile) => {

        HallTicketService.deleteFile({ id: fileDetails.id } as ICertificationDeleteRequest).then((result) => {
            if (result.data.errors && result.data.errors.length > 0) {
                toast.error(result.data.errors[0].message);
            } else {
                const filesData = certificationFiles.filter(i => i.id !== fileDetails.id);
                setCertificationFiles([...filesData]);
                toast.success("file deleted successfully");

            }

        }).finally(() => {

        });
    }
    const handleStudentsDownloadClick = (fileDetails: ICertificationFile) => {
        const fileDownloadUrl = HallTicketService.getStudentFileDownloadUrl(fileDetails.id);
        window.open(fileDownloadUrl, '_blank');
    }
    const handleHallTicketDownloadClick = (fileDetails: ICertificationFile) => {
        const fileDownloadUrl = HallTicketService.getHallTicketFileDownloadUrl(fileDetails.id);
        window.open(fileDownloadUrl, '_blank');
    }
    const handleGenerateHallTicketClick = (fileDetails: ICertificationFile) => {
        fileDetails.isInProgress = true;
        setCertificationFiles(certificationFiles =>
            certificationFiles.map(file =>
                file.id === fileDetails.id ? { ...file, ...fileDetails } : file
            )
        );

        HallTicketService.generateHallTicketFile({ id: fileDetails.id } as ICertificationGenerateRequest).then((result) => {
            if (result.data.errors && result.data.errors.length > 0) {
                toast.error(result.data.errors[0].message);
            } else {

                setCertificationFiles(certificationFiles =>
                    certificationFiles.map(file =>
                        file.id === fileDetails.id ? { ...file, ...result.data.data } : file
                    )
                );
                toast.success("HallTickets generated successfully");

            }

        }).finally(() => {

        });

    }


    return (
        <Box sx={{ width: '100%', height: '100%' }}>
            {isAddFilesVisible && (
                <CertificationFileUploadComponent
                    handleFileChange={handleFileUpload}
                    fileUploadIsInProgress={fileUploadIsInProgress}
                />
            )}
            <TableContainer sx={{ width: '100%', maxHeight: 'calc(70vh - 48px)', minHeight: '60vh', overflowY: 'auto' }}>
                {filesLoading && <LinearProgress sx={{ position: 'sticky', top: 0, zIndex: 2 }} />}
                <Table sx={{ minWidth: 650 }} aria-label="hall tickets table" stickyHeader>
                    <TableHead>
                        <TableRow>
                            <TableCell sx={{ fontWeight: 600, fontSize: '0.72rem', color: 'text.secondary', textTransform: 'uppercase', letterSpacing: '0.06em', bgcolor: 'grey.50' }}>Students File</TableCell>
                            <TableCell sx={{ fontWeight: 600, fontSize: '0.72rem', color: 'text.secondary', textTransform: 'uppercase', letterSpacing: '0.06em', bgcolor: 'grey.50' }}>Hall Ticket File</TableCell>
                            <TableCell sx={{ fontWeight: 600, fontSize: '0.72rem', color: 'text.secondary', textTransform: 'uppercase', letterSpacing: '0.06em', bgcolor: 'grey.50', width: 180 }}>Actions</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {certificationFiles.length === 0 && !filesLoading && (
                            <TableRow>
                                <TableCell colSpan={3} align="center">
                                    <Typography color="text.secondary" sx={{ py: 4 }}>No files uploaded yet.</Typography>
                                </TableCell>
                            </TableRow>
                        )}
                        {certificationFiles.map((programFile) => (
                            <TableRow key={programFile.id} sx={{ '&:hover': { bgcolor: 'action.hover' } }}>
                                <TableCell>
                                    <Stack direction="row" spacing={1} alignItems="center">
                                        <FileTypeIcon fileName={programFile.studentFileName} />
                                        <Typography sx={{ fontSize: '0.875rem' }}>{getFileNameWithOutExt(programFile.studentFileName)}</Typography>
                                        <Tooltip title="Download students file">
                                            <IconButton size="small" color="primary" onClick={() => handleStudentsDownloadClick(programFile)}>
                                                <CloudDownloadIcon fontSize="small" />
                                            </IconButton>
                                        </Tooltip>
                                    </Stack>
                                </TableCell>
                                <TableCell>
                                    {programFile.isInProgress && <LinearProgress sx={{ width: 120 }} />}
                                    {!programFile.isInProgress && programFile.hallTicketFileName && (
                                        <Stack direction="row" spacing={1} alignItems="center">
                                            <FileTypeIcon fileName={programFile.hallTicketFileName} />
                                            <Typography sx={{ fontSize: '0.875rem' }}>{getFileNameWithOutExt(programFile.hallTicketFileName)}</Typography>
                                            <Tooltip title="Download hall ticket file">
                                                <IconButton size="small" color="primary" onClick={() => handleHallTicketDownloadClick(programFile)}>
                                                    <CloudDownloadIcon fontSize="small" />
                                                </IconButton>
                                            </Tooltip>
                                        </Stack>
                                    )}
                                    {!programFile.isInProgress && !programFile.hallTicketFileName && (
                                        <Typography variant="body2" color="text.disabled" sx={{ fontStyle: 'italic' }}>Not generated</Typography>
                                    )}
                                </TableCell>
                                <TableCell sx={{ whiteSpace: 'nowrap', width: 200 }}>
                                    <Stack direction="row" spacing={1} alignItems="center">
                                        <Tooltip title="Delete this file">
                                            <span>
                                                <LoadingButton
                                                    loading={programFile.isDeleteInProgress}
                                                    variant="outlined"
                                                    color="error"
                                                    size="small"
                                                    startIcon={<DeleteIcon />}
                                                    onClick={() => handleFileDelete(programFile)}
                                                    sx={{ fontWeight: 500 }}
                                                >
                                                    Delete
                                                </LoadingButton>
                                            </span>
                                        </Tooltip>
                                        <Tooltip title="Generate hall ticket file">
                                            <span>
                                                <Button
                                                    variant="contained"
                                                    size="small"
                                                    disabled={programFile.isInProgress}
                                                    onClick={() => handleGenerateHallTicketClick(programFile)}
                                                    startIcon={<AutoFixHighIcon />}
                                                    sx={{ fontWeight: 600 }}
                                                >
                                                    Generate
                                                </Button>
                                            </span>
                                        </Tooltip>
                                    </Stack>
                                </TableCell>
                            </TableRow>
                        ))}
                    </TableBody>
                </Table>
            </TableContainer>
        </Box>
    );
};
